import { Dispatch, SetStateAction } from "react";
import { t } from "i18next";

import { FeatureTileDto } from "../../../../../../@api";

import { normalizeHex, normalizeHexOrLinearGradient } from "../../../../../../utils/functions/normalizeHex";
import { validateLength } from "../../../../../../utils/functions/normalizeLanguage";
import { isValidHttpUrl } from "../../../../../../utils/functions/validateHttpUrl";
import { translationKeys } from "../../../../../models/app-translation-keys";
import {
  AppLanguageOptionsSubset,
  AppLanguageOptionsSubsetEnum,
} from "../../../../../models/constants/app-language-options.const";
import {
  ISharedDialogAction,
  ISharedDialogActionContext,
  sharedDialog,
} from "../../../../Shared/Components/Dialogs/SharedDialog";
import {
  BUTTON_TEXT_MAX_LENGTH,
  createFeatureTileDto,
  DESCRIPTION_MAX_LENGTH,
  IFeatureTileEditorDialogState,
  TITLE_MAX_LENGTH,
} from "./featureTileEditorDialogTypes";

interface IFeatureTileEditorDialogFooterActionsProps {
  isEdit: boolean;
  disabled: boolean;
  defaultOrder: number;
  onCancel: () => void;
  onSave: (tile: FeatureTileDto) => Promise<void>;
}

const validateForm = (state: IFeatureTileEditorDialogState, disabled: boolean) => {
  const colorText = normalizeHex(state.colorTextRaw);
  const colorBackground = normalizeHexOrLinearGradient(state.colorBackgroundRaw);
  const colorBackgroundHover = normalizeHexOrLinearGradient(state.colorBackgroundHoverRaw);

  const { invalid: isTitleInvalid, tooLong: isTitleTooLong } = validateLength(state.title, TITLE_MAX_LENGTH);

  const { invalid: isDescriptionInvalid, tooLong: isDescriptionTooLong } = validateLength(
    state.description,
    DESCRIPTION_MAX_LENGTH,
  );

  const { invalid: isButtonTextInvalid, tooLong: isButtonTextTooLong } = validateLength(
    state.buttonText,
    BUTTON_TEXT_MAX_LENGTH,
  );

  const buttonTooltipEn = (state.buttonTooltip?.en ?? "").trim();
  const buttonTooltipDe = (state.buttonTooltip?.de ?? "").trim();
  const buttonTooltipFr = (state.buttonTooltip?.fr ?? "").trim();
  const isButtonTooltipInvalid =
    buttonTooltipEn.length === 0 || buttonTooltipDe.length === 0 || buttonTooltipFr.length === 0;

  const urlEn = (state.externalLinkUrl?.en ?? "").trim();
  const urlDe = (state.externalLinkUrl?.de ?? "").trim();
  const urlFr = (state.externalLinkUrl?.fr ?? "").trim();
  const isAnyUrlEmpty = urlEn.length === 0 || urlDe.length === 0 || urlFr.length === 0;

  let invalidUrlLangs: AppLanguageOptionsSubsetEnum[] = [];
  for (const lang of AppLanguageOptionsSubset) {
    const value = (state.externalLinkUrl?.[lang] ?? "").trim();
    if (value && !isValidHttpUrl(value)) {
      invalidUrlLangs.push(lang);
    }
  }
  const isAnyUrlInvalid = invalidUrlLangs.length > 0;

  const isTextColorInvalid = !!state.colorTextRaw.trim() && !colorText;
  const isBgColorInvalid = !!state.colorBackgroundRaw.trim() && !colorBackground;
  const isBgColorHoverInvalid = !!state.colorBackgroundHoverRaw.trim() && !colorBackgroundHover;

  const isFormInvalid =
    disabled ||
    isTitleInvalid ||
    isTitleTooLong ||
    isDescriptionInvalid ||
    isDescriptionTooLong ||
    isButtonTextInvalid ||
    isButtonTextTooLong ||
    isButtonTooltipInvalid ||
    isAnyUrlEmpty ||
    isAnyUrlInvalid ||
    isTextColorInvalid ||
    isBgColorInvalid ||
    isBgColorHoverInvalid;

  return {
    isFormInvalid,
    colorText,
    colorBackground,
    colorBackgroundHover,
  };
};

const featureKeys = translationKeys.adminArea.featureTiles;

export const featureTileEditorDialogFooterActions = ({
  isEdit,
  disabled,
  defaultOrder,
  onCancel,
  onSave,
}: IFeatureTileEditorDialogFooterActionsProps) => {
  return ({ id, sharedState, setSharedDialogState }: ISharedDialogActionContext): ISharedDialogAction[] => {
    const state = sharedState as IFeatureTileEditorDialogState;

    const setState = setSharedDialogState as Dispatch<SetStateAction<IFeatureTileEditorDialogState>>;

    const validation = validateForm(state, disabled);

    const handleSave = async () => {
      if (validation.isFormInvalid) {
        return;
      }

      setState(prev => ({ ...prev, isSaving: true }));

      const dto = createFeatureTileDto(state, defaultOrder, validation);

      try {
        await onSave(dto);

        if (id) {
          sharedDialog.closeSharedDialog(id);
        }
      } finally {
        setState(prev => ({ ...prev, isSaving: false }));
      }
    };

    return [
      {
        label: t(translationKeys.actions.cancel),
        isTextButton: true,
        isDisabled: state.isSaving,
        isLoading: false,
        tooltip: undefined,
        tooltipOptions: undefined,
        onClick: () => {
          onCancel();

          if (id) {
            sharedDialog.closeSharedDialog(id);
          }
        },
      },

      {
        label: isEdit ? t(translationKeys.actions.save) : t(translationKeys.actions.create),
        isTextButton: false,
        isDisabled: validation.isFormInvalid || state.isSaving,
        isLoading: state.isSaving,
        tooltip: validation.isFormInvalid ? t(featureKeys.validation.saveDisabled) : undefined,
        tooltipOptions: {
          position: "top",
          showOnDisabled: true,
          showDelay: 200,
        },
        onClick: handleSave,
      },
    ];
  };
};
